import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { COMPANY } from "../data/siteData";
import { imageUrl } from "../lib/images";

export default function HomePage() {
  return (
    <div className="bg-[#070d18] text-slate-100 min-h-screen">
      <SEOHead
        title="Køle- og medicinaltransport i hele Danmark"
        description="KVC Express ApS tilbyder transport af fødevarer, måltidskasser og medicin – inklusive Bornholm. Faste ruter og distribution for virksomheder."
      />

      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-28 overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(#1e3358_1px,transparent_1px)] [background-size:32px_32px] opacity-25 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0F766E]/15 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Content (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono">
                <span className="w-2 h-2 rounded-full bg-[#2DD4BF] animate-pulse"></span>
                <span>Faste transportaftaler for erhverv</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1] font-display">
                Køle- og medicinaltransport i hele Danmark
              </h1>

              <p className="text-base sm:text-lg text-slate-300 max-w-2xl font-normal leading-relaxed">
                KVC Express ApS tilbyder transport af fødevarer, måltidskasser og medicin – inklusive Bornholm. Vi hjælper virksomheder med faste ruter og distribution med fokus på korrekt temperatur, omhyggelig håndtering og levering til tiden.
              </p>

              {/* CTAs */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-[#0F766E] hover:bg-[#115E59] active:bg-[#134E4A] text-white font-bold text-sm uppercase tracking-wider transition shadow-xl shadow-[#0F766E]/30 cursor-pointer"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-slate-700 text-slate-200 hover:text-white font-semibold text-sm transition cursor-pointer"
                >
                  <i className="fa-solid fa-phone text-[#2DD4BF] text-xs" aria-hidden="true" />
                  <span>Ring til os</span>
                </a>
              </div>
            </div>

            {/* Right Graphic/Image Composition (5 cols) */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/80 bg-slate-900 shadow-2xl">
                <img
                  src={imageUrl("/_assets/img-3502-scaled.jpg", "width=1000,quality=85,format=auto")}
                  alt="KVC Express hvide kølebiler med aktivt køleanlæg på række"
                  className="w-full h-80 sm:h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070d18] via-transparent to-transparent opacity-75" />

                <div className="absolute bottom-4 inset-x-4 p-4 rounded-xl bg-[#0b1426]/90 backdrop-blur-md border border-slate-700 text-xs flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#0F766E] flex items-center justify-center text-white text-base shadow-sm">
                      <i className="fa-solid fa-snowflake text-[#2DD4BF]" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="font-bold text-white font-mono uppercase text-[11px]">Aktiv temperaturkontrol</div>
                      <div className="text-slate-300 text-[11px]">L2H2 kølevogne</div>
                    </div>
                  </div>
                  <div className="text-right font-mono text-[10px] text-[#2DD4BF] bg-[#0F766E]/30 px-2.5 py-1 rounded border border-[#2DD4BF]/40">
                    KØL & PHARMA
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. YDELSER (RÆKKEFØLGE: Køletransport, Fødevarer og måltidskasser, Medicin og apoteksvarer, Fast transportpartner) */}
      <section className="py-16 lg:py-24 bg-[#091122] border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest mb-3">
                <i className="fa-solid fa-layer-group text-[10px]" aria-hidden="true" />
                <span>Ydelser</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight font-display">
                Specialiseret transport til virksomheder
              </h2>
            </div>
            <p className="text-slate-400 text-sm max-w-md leading-relaxed">
              Faste distributionsaftaler og præcis temperaturstyring tilpasset jeres specifikke behov.
            </p>
          </div>

          {/* 4 Ydelser Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 1. Køletransport (Fremhævet) */}
            <div className="bg-gradient-to-br from-[#0a1b24] to-[#0b1528] border-2 border-[#0F766E]/80 rounded-2xl p-6 sm:p-8 flex flex-col justify-between shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#2DD4BF]/10 rounded-full blur-2xl pointer-events-none" />
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl bg-[#0F766E] text-white flex items-center justify-center shadow-md">
                    <i className="fa-solid fa-snowflake text-lg text-[#2DD4BF]" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-mono font-semibold px-2.5 py-1 rounded bg-[#0F766E]/30 text-[#2DD4BF] border border-[#2DD4BF]/30">
                    01 / KØL
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-display mb-3">
                  Køletransport
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Professionel transport af kølevarer med aktiv temperaturkontrol. Temperaturstyring tilpasset varernes krav og den aftalte transportopgave. Vi sikrer en ubrudt kølekæde på faste distributionsruter i hele landet for virksomheder med høje kvalitetskrav.
                </p>
              </div>
              <Link
                to="/ydelser/koeletransport"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#2DD4BF] hover:text-[#5EEAD4] group pt-2"
              >
                <span>Læs mere om køletransport</span>
                <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>

            {/* 2. Fødevarer og måltidskasser (Fremhævet) */}
            <div className="bg-gradient-to-br from-[#0a1b24] to-[#0b1528] border-2 border-[#0F766E]/80 rounded-2xl p-6 sm:p-8 flex flex-col justify-between shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#0F766E]/15 rounded-full blur-2xl pointer-events-none" />
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl bg-[#0F766E] text-white flex items-center justify-center shadow-md">
                    <i className="fa-solid fa-utensils text-lg text-[#2DD4BF]" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-mono font-semibold px-2.5 py-1 rounded bg-[#0F766E]/30 text-[#2DD4BF] border border-[#2DD4BF]/30">
                    02 / FØDEVARER
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-display mb-3">
                  Fødevarer og måltidskasser
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Pålidelig køletransport af måltidskasser, fersk kød, fisk og øvrige fødevarer. Vi lægger vægt på høj hygiejnestandard, omhyggelig varehåndtering og præcis levering til tiden.
                </p>
              </div>
              <Link
                to="/ydelser/foedevaretransport"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#2DD4BF] hover:text-[#5EEAD4] group pt-2"
              >
                <span>Læs mere om fødevaretransport</span>
                <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>

            {/* 3. Medicin og apoteksvarer (Tydelig og dedikeret) */}
            <div className="bg-[#0b1528] border border-slate-800 hover:border-slate-700 rounded-2xl p-6 sm:p-8 flex flex-col justify-between transition">
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#0F766E]/20 border border-[#0F766E]/50 text-[#2DD4BF] flex items-center justify-center">
                    <i className="fa-solid fa-pills text-base" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-mono text-slate-400">03 / PHARMA</span>
                </div>
                <h3 className="text-xl font-bold text-white font-display mb-3">
                  Medicin og apoteksvarer
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Specialiseret medicinaltransport for apoteker, sundhedsvirksomheder og klinikker. Chauffører med relevante kurser i medicintransport sikrer temperaturkontrol og skånsom håndtering.
                </p>
              </div>
              <Link
                to="/ydelser/medicinaltransport"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#2DD4BF] hover:text-[#5EEAD4] group pt-2"
              >
                <span>Læs mere om medicinaltransport</span>
                <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>

            {/* 4. Fast transportpartner */}
            <div className="bg-[#0b1528] border border-slate-800 hover:border-slate-700 rounded-2xl p-6 sm:p-8 flex flex-col justify-between transition">
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#0F766E]/20 border border-[#0F766E]/50 text-[#2DD4BF] flex items-center justify-center">
                    <i className="fa-solid fa-handshake text-base" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-mono text-slate-400">04 / PARTNER</span>
                </div>
                <h3 className="text-xl font-bold text-white font-display mb-3">
                  Fast transportpartner
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Vi indgår faste transportaftaler som stabil underleverandør for logistik- og distributionshuse. Vi stiller dedikerede køretøjer og erfarne chauffører til rådighed på faste ruter.
                </p>
              </div>
              <Link
                to="/ydelser/transportpartner"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#2DD4BF] hover:text-[#5EEAD4] group pt-2"
              >
                <span>Læs om transportpartnerskaber</span>
                <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FLÅDE & MATERIEL (Kompakt og overskuelig uden unødige gentagelser) */}
      <section className="py-16 lg:py-20 bg-slate-100 text-slate-900 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white border border-slate-300 text-slate-700 text-xs font-mono uppercase tracking-widest shadow-xs">
                <i className="fa-solid fa-van-shuttle text-[#0F766E] text-[11px]" aria-hidden="true" />
                <span>Vognpark</span>
              </div>

              <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-950 tracking-tight font-display">
                Moderne kølevogne med aktiv temperaturstyring
              </h2>

              <p className="text-slate-700 text-base leading-relaxed">
                Vores vognpark består af tidssvarende L2H2 varevogne (diesel og el) indrettet til professionel køletransport. Chaufførerne har lovpligtigt chaufføruddannelsesbevis og kurser i køle- og medicintransport, så jeres varer håndteres forsvarligt fra start til slut.
              </p>

              <div className="grid grid-cols-2 gap-3 pt-1 text-sm text-slate-800">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#0F766E] text-xs" aria-hidden="true" />
                  <span>Aktiv køl og temperaturstyring</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#0F766E] text-xs" aria-hidden="true" />
                  <span>Nye diesel- og elbiler</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#0F766E] text-xs" aria-hidden="true" />
                  <span>Uddannede chauffører</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#0F766E] text-xs" aria-hidden="true" />
                  <span>Høj hygiejnestandard</span>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/vores-koeretoejer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs uppercase tracking-wider font-semibold shadow-md transition cursor-pointer"
                >
                  <span>Læs mere om vores køretøjer</span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#2DD4BF]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden border border-slate-300 bg-white shadow-xl">
                <img
                  src={imageUrl("/_assets/shutterstock-2654580061.jpg", "width=900,quality=85,format=auto")}
                  alt="KVC Express flåde af moderne hvide varebiler"
                  className="w-full h-72 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. SÆRSKILT AFSNIT: ERFARING MED DAGLIG DISTRIBUTION (Punkt 3 fra kunden) */}
      <section className="py-16 lg:py-24 bg-[#081120] border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-slate-700 bg-slate-900 shadow-xl">
                <img
                  src={imageUrl("/_assets/img-4141.jpg", "width=800,quality=80,format=auto")}
                  alt="KVC Express kassevogn under kørsel ved boligområder til privatlevering"
                  className="w-full h-72 sm:h-96 object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest">
                <i className="fa-solid fa-truck-ramp-box text-[10px]" aria-hidden="true" />
                <span>Distributionserfaring</span>
              </div>

              <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight font-display">
                Erfaring med daglig distribution
              </h2>

              <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed">
                <p>
                  Som underleverandør for Gordon Delivery varetager KVC Express ApS den daglige distribution af måltidskasser fra blandt andre HelloFresh, Factor, RetNemt og BetterFeast samt medicin og apoteksvarer.
                </p>
                <p>
                  Erfaringen fra denne faste rutedistribution sikrer høj præcision, ubrudt kølekæde og professionel service, som vi tager med ind i nye samarbejder og transportpartnerskaber.
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/ydelser/transportpartner"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#2DD4BF] hover:text-[#5EEAD4] group"
                >
                  <span>Læs mere om samarbejde og transportpartnerskaber</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. NEDERST PÅ FORSIDEN (Punkt 5 fra kunden) */}
      <section className="py-20 lg:py-28 bg-[#050a14]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest">
            <i className="fa-solid fa-handshake text-[10px]" aria-hidden="true" />
            <span>Samarbejde</span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight font-display">
            Skal vi være jeres næste transportpartner?
          </h2>

          <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Kontakt os for en uforpligtende samtale om køletransport, fødevaredistribution eller medicinaltransport.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-[#0F766E] hover:bg-[#115E59] text-white font-bold text-xs uppercase tracking-wider transition shadow-xl shadow-[#0F766E]/40 cursor-pointer"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-200 hover:text-white font-semibold text-sm transition whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#2DD4BF] text-xs" aria-hidden="true" />
              <span>Ring på 29 36 87 88</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
